// Dangerous-query risk gate (WS-E — StructuredSqlAdoption Slice 13, core
// surface). Replaces the legacy raw-buffer gate that lived in
// engineCapabilities.ts (naive `;` splitter + routine-body stripper, deleted
// with WS-E): the per-engine rule tables are evaluated per GRAMMAR statement
// slice (`splitForExecution`)
// over a whole-buffer CODE-ONLY projection (`projectCodeOnly`), with:
//   • stored routine definitions skipped (bodies inert at submission time —
//     the ONLY suppression; `classifyStatement().risk` is metadata, never a
//     gate, per CK-E "unknown/session/call all carry important rules"),
//   • immediate procedural blocks (PG DO, Oracle anonymous BEGIN/DECLARE)
//     INSPECTED per inner `;` fragment (a WHERE in one inner statement must
//     not protect another),
//   • ALL rules evaluated on ALL slices — highest severity wins; ties break
//     to the earliest buffer position, then stable table order (CK-E #4),
//   • projection GAPS no statement slice covers (leading / inter-statement /
//     trailing) are ALSO evaluated when they hold revealed code — a mysql
//     executable comment (`/*! … */`) EXECUTES but the frozen scanner emits no
//     statement for it, so a `;`-separated payload after a real statement is
//     otherwise unscanned; a gap hit reports statementIndex 0. The
//     zero-statement comment-only buffer is the degenerate whole-buffer gap,
//   • ambiguity (scanner OR projection) → high `parse-ambiguous`, no
//     statement attribution,
//   • the returned preview is ORIGINAL source, never the masked projection.
//
// Known, documented false-negative (CK-E §1): semicolonless T-SQL
// multi-statement batches remain ONE slice, so a later WHERE can still
// suppress an earlier DELETE — solving that needs full T-SQL parsing.

import type { DangerousQueryResult, DatabaseEngineId } from '../../core/engines';
import { projectCodeOnly } from '../sql-grammar/document/codeProjection';
import {
  hasDataLossReviewLine,
  dataLossStatementPreview,
} from '../../core/engines/scriptDataLossMarker';
import { splitForExecution, type ExecutableStatement } from '../sql-grammar/document/splitForExecution';
import type { SqlEngine } from '../sql-grammar/model';
import { classifyExecutionContext } from './executionContext';
import {
  compileRules,
  GRAMMAR_ENGINE_BY_ID,
  MERGE_TAUTOLOGY_PROSE,
  parseOptionsFor,
  patternsFor,
  proseFor,
  TAUTOLOGY_PROSE,
  type RiskRule,
} from './riskRules';

const PARSE_AMBIGUOUS: DangerousQueryResult = {
  dangerous: true,
  severity: 'high',
  kind: 'unparseable',
  reason: proseFor('parse-ambiguous'),
  reasonClass: 'parse-ambiguous',
};

// A WHERE clause whose ENTIRE boolean expression is the 1=1 tautology. The
// tautology may be followed by end-of-fragment, `;`, or a trailing clause
// keyword (`ORDER BY`, `LIMIT`, `RETURNING`, …) — those terminate the boolean
// expression just as the fragment end does, so `DELETE … WHERE 1=1 ORDER BY id`
// is still unbounded (F4). A real boolean continuation (`AND`/`OR`) instead of
// a clause keyword means a genuine filter, so `WHERE 1=1 AND id = 5` must NOT
// match.
const WHERE_TAUTOLOGY =
  /\bWHERE\s+\(?\s*1\s*=\s*1\s*\)?\s*(?:;|$|\b(?:ORDER|GROUP|HAVING|LIMIT|OFFSET|FETCH|RETURNING|WINDOW|INTO|FOR|OPTION|UNION|EXCEPT|INTERSECT)\b)/i;
// A MERGE whose ON condition is the bare 1=1 tautology (next token WHEN/end).
const MERGE_HEAD = /^\s*MERGE\b/i;
const MERGE_ON_TAUTOLOGY = /\bON\s*\(?\s*1\s*=\s*1\s*\)?\s*(?:WHEN\b|;|$)/i;

/**
 * The object keywords a routine-drop rule actually matched, lower-cased, in
 * first-appearance order. Read out of the firing rule's OWN capture group, so
 * it can never disagree with the verdict that produced it. The dialog turns
 * these into user vocabulary ("stored procedure") — being told "Routine" and
 * having to work out what that meant mid-decision is the reported complaint.
 */
function collectDroppedObjectTypes(pattern: RegExp, scannable: string): string[] {
  const global = new RegExp(
    pattern.source,
    pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`,
  );
  const seen: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = global.exec(scannable)) !== null) {
    const keyword = match[1]?.toLowerCase();
    if (keyword && !seen.includes(keyword)) seen.push(keyword);
    // A zero-length match would spin forever; every alternation here consumes
    // at least `DROP`, but the guard costs nothing.
    if (match.index === global.lastIndex) global.lastIndex++;
  }
  return seen;
}

/**
 * `reasonClass` is this gate's stable classification; `kind` is the vocabulary
 * the confirmation dialog reads. They are separate fields on
 * `DangerousQueryResult` and both are reported, so neither consumer has to
 * translate the other's.
 */
const KIND_BY_REASON_CLASS: Record<
  NonNullable<DangerousQueryResult['reasonClass']>,
  NonNullable<DangerousQueryResult['kind']>
> = {
  'parse-ambiguous': 'unparseable',
  destructive: 'destructive-ddl',
  'routine-drop': 'routine-drop',
  'unbounded-dml': 'missing-where',
  'system-command': 'engine-specific',
  'unsupported-management': 'engine-specific',
};

interface Candidate {
  readonly severity: 'high' | 'low';
  readonly reasonClass: DangerousQueryResult['reasonClass'];
  /** The firing rule, kept so `routine-drop` can name every object the SCRIPT
   *  drops (not just the slice that won) — see `collectDroppedObjectTypes`. */
  readonly droppedRulePattern?: RegExp;
  readonly reason: string;
  readonly patternSource: string;
  /** Buffer offset of the owning region — the position tie-break key (earliest
   *  wins). Decoupled from `statementIndex` so a GAP candidate slots into
   *  buffer order without borrowing a statement's ordinal. For statements this
   *  equals `startOffset`, whose order matches the flat statement index, so the
   *  tie-break is unchanged for statement-only buffers. */
  readonly orderKey: number;
  /** Reported matched-statement ordinal: the statement's flat index, or 0 for
   *  a gap region (consistent with the zero-statement companion it subsumes). */
  readonly statementIndex: number;
  /** Reported preview — ORIGINAL source, never the masked projection. */
  readonly preview: string;
  readonly ruleOrder: number;
}

/** Positional + attribution info shared by every candidate a fragment yields. */
interface FragmentSite {
  readonly orderKey: number;
  readonly statementIndex: number;
  readonly preview: string;
}

const severityRank = (s: 'high' | 'low'): number => (s === 'high' ? 0 : 1);

function better(a: Candidate, b: Candidate): boolean {
  if (severityRank(a.severity) !== severityRank(b.severity)) {
    return severityRank(a.severity) < severityRank(b.severity);
  }
  if (a.orderKey !== b.orderKey) return a.orderKey < b.orderKey;
  return a.ruleOrder < b.ruleOrder;
}

/** First dollar-quoted section of a PG DO block — its immediately-executed
 *  body. Returns null when the block has no dollar-quoted body. */
function extractDollarBody(original: string): string | null {
  const open = original.match(/\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
  if (!open || open.index === undefined) return null;
  const tag = open[0];
  const start = open.index + tag.length;
  const end = original.indexOf(tag, start);
  return end < 0 ? null : original.slice(start, end);
}

/** Evaluate every rule against one code-only fragment. `site` carries the
 *  position (tie-break), reported ordinal, and preview shared by its matches. */
function evaluateFragment(
  fragment: string,
  rules: readonly RiskRule[],
  site: FragmentSite,
  push: (c: Candidate) => void,
): void {
  rules.forEach((rule, ruleOrder) => {
    if (!rule.pattern.test(fragment)) return;
    if (rule.requiresNoRowFilter) {
      const hasWhere = /\bWHERE\b/i.test(fragment);
      if (hasWhere && !WHERE_TAUTOLOGY.test(fragment)) return;
      push({
        severity: rule.severity,
        reasonClass: rule.reasonClass,
        reason: hasWhere ? TAUTOLOGY_PROSE : proseFor('unbounded-dml'),
        patternSource: rule.pattern.source,
        ...site,
        ruleOrder,
      });
      return;
    }
    push({
      severity: rule.severity,
      reasonClass: rule.reasonClass,
      reason: proseFor(rule.reasonClass),
      patternSource: rule.pattern.source,
      ...(rule.reasonClass === 'routine-drop' ? { droppedRulePattern: rule.pattern } : {}),
      ...site,
      ruleOrder,
    });
  });
  // Gate-level tautology check for MERGE (adopted CK-E policy) — cheap
  // sibling of the WHERE tautology above; ordered after every table rule.
  if (MERGE_HEAD.test(fragment) && MERGE_ON_TAUTOLOGY.test(fragment)) {
    push({
      severity: 'high',
      reasonClass: 'unbounded-dml',
      reason: MERGE_TAUTOLOGY_PROSE,
      patternSource: MERGE_ON_TAUTOLOGY.source,
      ...site,
      ruleOrder: rules.length,
    });
  }
}

/** Projection regions (leading / inter-statement / trailing) that NO statement
 *  slice covers. A gap holds non-blank PROJECTED content only when
 *  `projectCodeOnly` REVEALED code there — today exclusively mysql
 *  executable-comment payloads, which EXECUTE (the frozen scanner treats them
 *  as inert block comments and emits no statement, so a `;`-separated payload
 *  after a real statement is otherwise never scanned). The whole buffer is one
 *  gap when the scan produced no statement. */
function uncoveredGaps(
  statements: readonly ExecutableStatement[],
  length: number,
): Array<{ readonly start: number; readonly end: number }> {
  const gaps: Array<{ start: number; end: number }> = [];
  const sorted = [...statements].sort((a, b) => a.startOffset - b.startOffset);
  let cursor = 0;
  for (const s of sorted) {
    if (s.startOffset > cursor) gaps.push({ start: cursor, end: s.startOffset });
    cursor = Math.max(cursor, s.endOffset);
  }
  if (cursor < length) gaps.push({ start: cursor, end: length });
  return gaps;
}

/** Code-only fragments to evaluate for one slice. Immediate procedural blocks
 *  are split at inner `;` so per-fragment WHERE checks stay honest; PG DO
 *  bodies are extracted from the ORIGINAL text and re-projected. Returns null
 *  when a body projection is ambiguous (conservative). */
function fragmentsFor(
  stmt: ExecutableStatement,
  sliceProjection: string,
  context: ReturnType<typeof classifyExecutionContext>,
  engine: SqlEngine,
): string[] | null {
  if (context !== 'immediate-procedural-block') return [sliceProjection];
  if (engine === 'postgres') {
    const body = extractDollarBody(stmt.text);
    if (body === null) return [sliceProjection];
    const bodyProjection = projectCodeOnly(body, engine);
    if (bodyProjection.ambiguous) return null;
    return bodyProjection.code.split(';');
  }
  return sliceProjection.split(';');
}

function checkKustoRisk(query: string): DangerousQueryResult {
  const patterns = patternsFor('kusto');
  if (!patterns) return { dangerous: false };
  for (const rule of compileRules(patterns)) {
    if (rule.requiresNoRowFilter || !rule.pattern.test(query)) continue;
    return {
      dangerous: true,
      severity: rule.severity,
      reason: patterns.getConfirmationMessage?.(query, rule.pattern.source) ?? proseFor(rule.reasonClass),
      pattern: rule.pattern.source,
      statement: query.trim().substring(0, 100),
      kind: KIND_BY_REASON_CLASS[rule.reasonClass],
      reasonClass: rule.reasonClass,
      statementIndex: 0,
      ...(rule.reasonClass === 'routine-drop'
        ? (() => {
            const droppedObjectTypes = collectDroppedObjectTypes(rule.pattern, query);
            return droppedObjectTypes.length > 0 ? { droppedObjectTypes } : {};
          })()
        : {}),
    };
  }
  return { dangerous: false };
}

/**
 * Check whether `sql` contains dangerous operations for `engineId`.
 * Drop-in successor of the legacy engineCapabilities gate (note the flipped
 * argument order) — returns the same `DangerousQueryResult` shape, so
 * `DangerousQueryDialog` is untouched.
 */
export const checkRisk = (sql: string, engineId: DatabaseEngineId): DangerousQueryResult => {
  if (!sql.trim()) return { dangerous: false };

  // The generator-disclosed column data-loss tag (#37 / #46 / #73), checked
  // BEFORE the code-only projection because it is carried on a `--` comment
  // line and the projection strips comments — this is the only point at which
  // the tag is visible at all.
  //
  // It is a marker rather than a pattern because an `ALTER COLUMN` statement's
  // text is byte-identical for a lossy narrowing and a lossless widening: the
  // before-state simply is not in the statement. The verdict is computed from
  // the comparer's before/after snapshots and travels here as a tag on the
  // `-- [REVIEW]` line. See `scriptDataLossMarker.ts` for the full contract.
  //
  // It also wins over a DROP elsewhere in the same script: both would confirm,
  // this is the more specific of the two, and it names the loss that has no
  // other warning anywhere.
  if (hasDataLossReviewLine(sql)) {
    return {
      dangerous: true,
      severity: 'high',
      kind: 'column-data-loss',
      reason: 'This statement destroys data already stored in a column',
      statement: dataLossStatementPreview(sql),
    };
  }

  if (engineId === 'kusto') return checkKustoRisk(sql);

  const grammarEngine = GRAMMAR_ENGINE_BY_ID[engineId];
  const patterns = patternsFor(engineId);
  if (!grammarEngine || !patterns) return { dangerous: false };

  // The PROJECTION is the ambiguity authority: it models dialect escapes the
  // statement scanner deliberately doesn't (mysql backslash strings — CK-E's
  // "fix dialect projection gaps before relying on it for safety"). A scanner-
  // only ambiguity (split.ambiguous with a clean projection) falls through:
  // the scanner then returns the whole buffer as ONE unit (Decision 4), and
  // rules are evaluated over that single slice's clean projection.
  const bufferProjection = projectCodeOnly(sql, grammarEngine);
  if (bufferProjection.ambiguous) return PARSE_AMBIGUOUS;
  const split = splitForExecution(sql, parseOptionsFor(grammarEngine));

  const rules = compileRules(patterns);
  // Flatten to executable statements: for mssql one GO batch may carry several
  // scanner statements (iterate unit.statements, not unit.sql); `GO N` repeats
  // execute the same source — inspect it ONCE.
  const statements = split.units.flatMap((u) => u.statements);

  let best: Candidate | null = null;
  const push = (c: Candidate): void => {
    if (best === null || better(c, best)) best = c;
  };

  // Oracle: `CREATE … AS <decls>; BEGIN … END;` splits into a stored-def
  // header slice followed by its (bare-BEGIN) body slice — the body belongs
  // to the definition and stays inert. Only armed when the header actually
  // OPENS a declaration section (`IS`/`AS`) and carries no BEGIN of its own —
  // a body-less routine-DDL like `ALTER FUNCTION f COMPILE` has neither and
  // must NOT swallow a genuine anonymous block that follows it (F1: anonymous
  // blocks are never skipped — CK-E mandatory).
  let pendingOracleRoutineBody = false;

  for (let i = 0; i < statements.length; i++) {
    const stmt = statements[i];
    const sliceProjection = bufferProjection.code.slice(stmt.startOffset, stmt.endOffset);
    const context = classifyExecutionContext(sliceProjection, grammarEngine);

    if (context === 'stored-routine-definition') {
      pendingOracleRoutineBody =
        grammarEngine === 'oracle' &&
        /\b(?:IS|AS)\b/i.test(sliceProjection) &&
        !/\bBEGIN\b/i.test(sliceProjection);
      continue; // body inert at submission time — the ONLY suppression.
    }
    if (pendingOracleRoutineBody && context === 'immediate-procedural-block') {
      pendingOracleRoutineBody = false;
      continue; // the stored definition's own body slice.
    }
    pendingOracleRoutineBody = false;

    const fragments = fragmentsFor(stmt, sliceProjection, context, grammarEngine);
    if (fragments === null) return PARSE_AMBIGUOUS;
    const site: FragmentSite = {
      orderKey: stmt.startOffset,
      statementIndex: i,
      preview: stmt.text.trim().substring(0, 100),
    };
    for (const fragment of fragments) evaluateFragment(fragment, rules, site, push);
  }

  // Gap regions the statement scan left uncovered (leading / inter-statement /
  // trailing). The frozen scanner emits NO statement for a mysql executable
  // comment (`/*! … */`), so a `;`-separated destructive payload that FOLLOWS a
  // real statement sits in one of these gaps — yet the projection REVEALS it as
  // code (it EXECUTES). Evaluating every NON-BLANK gap closes that bypass
  // engine-generically (no mysql special-case here): on engines without
  // executable comments a gap only ever holds blanks (masked comments/strings)
  // or inert separators (`;`/`GO`/`/`), which match no rule, so this adds no
  // false-positive surface. A gap hit reports statementIndex 0 and previews the
  // gap's ORIGINAL text — for the zero-statement comment-only buffer that
  // whole-buffer gap yields the exact shape of the fix-r2 companion it replaces.
  for (const gap of uncoveredGaps(statements, sql.length)) {
    const gapCode = bufferProjection.code.slice(gap.start, gap.end);
    if (!/\S/.test(gapCode)) continue;
    const site: FragmentSite = {
      orderKey: gap.start,
      statementIndex: 0,
      preview: sql.slice(gap.start, gap.end).trim().substring(0, 100),
    };
    evaluateFragment(gapCode, rules, site, push);
  }

  if (best === null) return { dangerous: false };
  const chosen: Candidate = best;
  return {
    dangerous: true,
    severity: chosen.severity,
    kind: chosen.reasonClass ? KIND_BY_REASON_CLASS[chosen.reasonClass] : undefined,
    reason: chosen.reason,
    pattern: chosen.patternSource,
    statement: chosen.preview,
    reasonClass: chosen.reasonClass,
    statementIndex: chosen.statementIndex,
    ...(() => {
      if (!chosen.droppedRulePattern) return {};
      const droppedObjectTypes = collectDroppedObjectTypes(
        chosen.droppedRulePattern,
        bufferProjection.code,
      );
      return droppedObjectTypes.length > 0 ? { droppedObjectTypes } : {};
    })(),
  };
};
